import { CreditCardOutlined, InfoCircleOutlined } from "@ant-design/icons"
import { ApprovalScopeEnum, CurrencyEnum, PurchaseOrderDTO, PurchaseOrderLineItemDTO, RightEnum, UserRule, type PurchaseOrderOcrResult } from "@finway-group/shared/lib/models"
import { type DocumentCoreData } from "@finway-group/shared/lib/models/file/documentCoreData"
import {
    PURCHASE_ORDER_DESCRIPTION_MAX_LENGTH,
    PURCHASE_ORDER_DESCRIPTION_MIN_LENGTH,
    PURCHASE_ORDER_NUMBER_MAX_LENGTH,
    PURCHASE_ORDER_NUMBER_MIN_LENGTH,
    PURCHASE_ORDER_PAYMENT_TERMS_MAX_LENGTH,
    PURCHASE_ORDER_PAYMENT_TERMS_MIN_LENGTH,
} from "@finway-group/shared/lib/models/purchaseOrder/purchaseOrder.validation"
import { Button, Col, DatePicker, Form, Input, InputRef, Row, Select, Switch, Tooltip } from "antd"
import moment from "moment"
import React, { LegacyRef, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { ApprovalProcessIndicatorTag } from "~Components/approvalProcessesIndicator"
import { CurrencyInput } from "~Components/currencyInput"
import { CostCenter2Select } from "~Components/form/CostCenter2Select"
import { CostCenterSelect } from "~Components/form/CostCenterSelect"
import { ExpenseAccountSelect } from "~Components/form/ExpenseAccountSelect"
import FormTaxRateSelector from "~Components/form/formTaxRateSelector"
import { CurrencySelect } from "~Components/forms/expenseForm/commonInputFields/CurrencySelect"
import { VendorSelection } from "~Components/forms/expenseForm/commonInputFields/VendorSelection"
import { HoverableInfo } from "~Components/forms/expenseForm/reimbursementForms/auxilliary/HoverableInfo"
import { ResponsiveFormLayout } from "~Components/layout/ResponsiveFormLayout"
import { EmployeeRuleModal } from "~Components/modals/employeeRule.modal"
import { UserImage } from "~Components/UserImage"
import { NO_TAX_RATE } from "~Shared/config/consts"
import { useModal } from "~Shared/context/modal.context"
import { useCompany } from "~Shared/hooks/company.hooks"
import { useSubCostCenters } from "~Shared/hooks/costCenter.hooks"
import { useCostCenters2 } from "~Shared/hooks/costCenter2.hooks"
import { useExpenseAccounts } from "~Shared/hooks/expenseAccount.hooks"
import { useTaxes } from "~Shared/hooks/tax.hooks"
import { useReplacementForAbsentApprover } from "~Shared/hooks/useReplacementForAbsentApprover.hook"
import { useLoggedInEmployeeProfile } from "~Shared/queries/employee.queries"
import { useVendor, useVendorList } from "~Shared/queries/vendors/vendor.queries"
import { AuthzService, ExpenseService, NotificationService } from "~Shared/services"
import { NotificationTypeEnum } from "~Shared/services/notification.service"
import { VendorReadFileAiResponse } from "~Shared/services/ocr.service"
import { getNextApproverORFirstNotSkippedApprover } from "~Shared/utils/approvalProcess.utils"
import { gobdNonCompliantDates } from "~Shared/utils/date.utils"
import { getSubstitutionTooltipMessage } from "~Shared/utils/employee.utils"
import { AccountingRule, calculateAccountingRules, formatAddress, formatDeliveryLocation } from "~Shared/utils/form.utils"
import { doAllElementsHaveTheSameFieldValue, isSet } from "~Shared/utils/helper.utils"
import { mapVendorReadFileAiResponseToVendor } from "~Shared/utils/ocr.utils"
import { getPopupAnchor } from "~Shared/utils/popup.utils"
import { lazily } from "~Shared/utils/react.utils"
import { useLineItems } from "../hooks/useLineItem.hook"
import { approvalProcessDependentFields, usePurchaseOrderApprovals } from "../hooks/usePurchaseOrderApprovals.hook"
import { usePurchaseOrdersEmployees } from "../hooks/usePurchaseOrderEmployees.hook"
import { useMutatePurchaseOrders } from "../hooks/usePurchaseOrders.hook"
import { SaveAsDraftIcon } from "../icons/SaveAsDraftIcon"
import { ServiceIcon } from "../icons/ServiceIcon"
import { FormValue, ILineItem } from "../types/LineItems.interfaces"
import { PurchaseOrder, PurchaseOrderFormValues } from "../types/PurchaseOrder.interfaces"
import { isPurchaseOrderApproverVisible, resolveTax } from "../types/utils/helper.util"
import { FileZone } from "./FileZone"
import { LineItemsList } from "./LineItemsList"
import { PurchaseOrderAddressAutocomplete } from "./PurchaseOrderAddressAutoComplete"
import { createPurchaseOrderFormRules } from "./PurchaseOrderForm.rules"
import { PurchaseOrderTags } from "./PurchaseOrderTags"

const { ReplacementForAbsentApproverModal } = lazily(() => import("../../../../components/modals/replacementForAbsentApprover.modal"))

const toFormValues = (purchaseOrder: PurchaseOrder | undefined): PurchaseOrderFormValues | undefined => {
    if (!purchaseOrder) {
        return undefined
    }
    return {
        ...purchaseOrder,
        dateOfIssueForm: isSet(purchaseOrder.dateOfIssue) ? moment(purchaseOrder.dateOfIssue) : undefined,
        expectedFullfilmentDateForm: isSet(purchaseOrder.expectedFullfilmentDate) ? moment(purchaseOrder.expectedFullfilmentDate) : undefined,
        performancePeriod:
            isSet(purchaseOrder.performanceDateFrom) && isSet(purchaseOrder.performanceDateTo)
                ? [moment(purchaseOrder.performanceDateFrom), moment(purchaseOrder.performanceDateTo)]
                : undefined,
        taxRate: "",
    }
}

interface PurchaseOrderFormProps {
    editMode: boolean
    purchaseOrder?: PurchaseOrder
    onCancel: () => void
    disabled?: boolean
    focusCatcher: React.RefObject<InputRef>
}

const MINIMUM_EMPLOYEE_RULES = 1

export const PurchaseOrderForm = ({ editMode, purchaseOrder, onCancel, disabled, focusCatcher }: PurchaseOrderFormProps) => {
    const initialValues = useMemo(() => toFormValues(purchaseOrder), [purchaseOrder])
    const { t } = useTranslation()
    const purchaseOrderFormRules = createPurchaseOrderFormRules()
    const { showModal } = useModal()
    const company = useCompany()
    const loggedInUser = useLoggedInEmployeeProfile()
    const { createPO, updatePO, createAndSubmitPO, createPurchaseOrderLoading, createAndSubmitPurchaseOrderLoading, updatePurchaseOrderLoading } = useMutatePurchaseOrders()

    // Form instances
    const [form] = Form.useForm<PurchaseOrderFormValues>()

    const {
        buildPurchaseOrderApprovalProcess,
        buildPurchaseOrderApprovalProcessWithApprover,
        approvalProcesses,
        setApprovalProcesses,
        workflow,
        confirmApprovalProcessChange,
        isBuildingApprovalProcess,
    } = usePurchaseOrderApprovals(form, purchaseOrder)

    const { getProposedApprovers, employees, eligibleRequesters } = usePurchaseOrdersEmployees()

    // Replacement for absent approver
    const {
        isReplacementForAbsentApproverShowing,
        absentApprovers,
        availableReplacementsForAbsentApprover,
        replacementForAbsentApprover,
        checkForAbsentApprovers,
        setCheckIfApproverInAbsence,
        setReplacementForAbsentApprover,
        closeModal: closeReplacementModal,
    } = useReplacementForAbsentApprover({ employees })
    const pendingCreatePORef = useRef<typeof createPO | typeof createAndSubmitPO | null>(null)

    const prepareData = async (values: PurchaseOrderFormValues) => {
        const { deliveryLocation } = values

        const location = formatDeliveryLocation(deliveryLocation)

        const poApprovalProcesses = await confirmApprovalProcessChange(true)

        const po: PurchaseOrderDTO = {
            createCard: values.createCard ?? false,
            currency: values.currency,
            dateOfIssue: values.dateOfIssueForm?.toDate() ?? null,
            deliveryLocation: isSet(location) ? location : null,
            description: values.description,
            expectedFullfilmentDate: values.expectedFullfilmentDateForm?.toDate() ?? null,
            isService: values.isService ?? false,
            lineItems: values.lineItems.map(
                (formLineItem: ILineItem): PurchaseOrderLineItemDTO => ({
                    costCenter: formLineItem.costCenter,
                    costCenter2: isSet(formLineItem.costCenter2) ? formLineItem.costCenter2 : null,
                    expenseAccount: isSet(formLineItem.expenseAccount) ? formLineItem.expenseAccount : null,
                    link: isSet(formLineItem.link) ? formLineItem.link : null,
                    materialCode: isSet(formLineItem.materialCode) ? formLineItem.materialCode : null,
                    name: formLineItem.name,
                    note: isSet(formLineItem.note) ? formLineItem.note : null,
                    quantity: formLineItem.quantity,
                    taxRate: formLineItem.taxRate?._id === NO_TAX_RATE ? undefined : formLineItem.taxRate?._id,
                    totalGrossPrice: formLineItem.totalGrossPrice,
                    totalNetPrice: formLineItem.totalNetPrice,
                    totalTaxPrice: formLineItem.totalTaxPrice,
                    unitOfMeasure: isSet(formLineItem.unitOfMeasure) ? formLineItem.unitOfMeasure : null,
                    unitPrice: formLineItem.unitPrice,
                }),
            ),
            paymentTerms: isSet(values.paymentTerms) ? values.paymentTerms : null,
            performancePeriod: values.performancePeriod ? [values.performancePeriod[0]?.toDate(), values.performancePeriod[1]?.toDate()] : null,
            purchaseOrderDocument: values.purchaseOrderDocument,
            purchaseOrderNumber: isSet(values.purchaseOrderNumber) ? values.purchaseOrderNumber : undefined,
            requestedBy: isSet(values.requestedBy?._id) ? values.requestedBy._id : purchaseOrder?.requestedBy?._id!,
            tags: values.tags ?? [],
            totalGrossPrice: values.totalGrossPrice,
            totalNetPrice: values.totalNetPrice,
            totalTaxPrice: values.totalTaxPrice,
            vendor: values.vendor?._id,
            approvalProcesses: poApprovalProcesses ?? [],
        }

        return po
    }

    const onSubmit =
        ({
            createPurchaseOrderFn,
            checkAndExpandErrors,
            shouldCheckAbsentApprovers,
        }: {
            createPurchaseOrderFn: typeof createPO | typeof createAndSubmitPO
            checkAndExpandErrors: () => void
            shouldCheckAbsentApprovers: boolean
        }) =>
        async () => {
            try {
                // 1. Validate form fields
                const validatedValues = await form.validateFields()

                // ReplacementForAbsentApproverModal submit need to know the actual create function after confirmed
                pendingCreatePORef.current = createPurchaseOrderFn

                // 2. absent approvers check
                if (shouldCheckAbsentApprovers) {
                    const mergedApprovalProcesses = await confirmApprovalProcessChange(false)
                    const hasAbsentApprovers = await checkForAbsentApprovers({
                        entity: {
                            requestedBy: validatedValues.requestedBy,
                            approvalProcesses: mergedApprovalProcesses,
                        },
                        scope: ApprovalScopeEnum.PURCHASE_ORDER,
                    })
                    if (hasAbsentApprovers) {
                        // model showing
                        return
                    }
                }

                // 3. form data prepare
                const po = await prepareData(validatedValues)
                po.replacementForAbsentApprover = replacementForAbsentApprover || undefined

                // 4. mutation
                const onMutationSuccess = () => {
                    onCancel()
                }

                if (editMode && purchaseOrder?._id) {
                    updatePO(purchaseOrder._id, po, onMutationSuccess)
                } else {
                    createPurchaseOrderFn(po, onMutationSuccess)
                }
            } catch (error) {
                const isFormValidateFieldsError = error.errorFields
                if (isFormValidateFieldsError) {
                    const firstError = isFormValidateFieldsError[0]
                    const fieldKey = firstError.name

                    form.scrollToField(fieldKey, { block: "center", behavior: "smooth" })

                    checkAndExpandErrors()
                    return
                }
                NotificationService.send(NotificationTypeEnum.ERROR, t("error:unknown"), undefined, 3)
            }
        }

    const globalCurrency = loggedInUser?.settings?.globalCurrency ?? CurrencyEnum.EUR
    const [currency, setCurrency] = useState<CurrencyEnum>(initialValues?.currency ?? globalCurrency)
    const {
        lineItems,
        expandedItems,
        addLineItem,
        removeLineItem,
        toggleExpand,
        updateLineItem,
        updateAllLineItems,
        setExpandedItems,
        setLineItems,
        isCostCenterUniform,
        isCostCenter2Uniform,
        isExpenseAccountUniform,
        checkAndExpandErrors,
    } = useLineItems(form, initialValues?.lineItems)

    const taxValues = useTaxes()
    const costCenters = useSubCostCenters(true)
    const costCenters2 = useCostCenters2()
    const expenseAccounts = useExpenseAccounts()

    const { vendorRulesOverwriteEmployeeRulesEnabled } = company

    const [initValueSet, setInitValueSet] = useState<boolean>(false)
    const [isService, setIsService] = useState(initialValues?.isService ?? false)
    const [currentFile, setCurrentFile] = useState<DocumentCoreData | undefined>(initialValues?.purchaseOrderDocument)
    const [currentFileOcr, setCurrentFileOcr] = useState<VendorReadFileAiResponse | undefined>(undefined)
    const [localVendor, setLocalVendor] = useState<string | undefined>(initialValues?.vendor?._id || undefined)
    const [localRequestedBy, setLocalRequestedBy] = useState<string | undefined>(initialValues?.requestedBy?._id ?? loggedInUser?.id)
    const [userRuleCount, setUserRuleCount] = useState(0)

    const { data: vendorList } = useVendorList({})
    const { data: selectedVendor } = useVendor(localVendor, true)

    const isUserAllowedToCreatePOsForOthers = AuthzService.isLoggedInUserAllowedToCreatePurchaseOrders()

    const tags = Form.useWatch("tags", form)
    const requestedBy = Form.useWatch("requestedBy", form)

    useEffect(() => {
        if (initValueSet) {
            return
        }
        setInitValueSet(true)

        if (initialValues?.performanceDateFrom && initialValues?.performanceDateTo) {
            form.setFieldValue("performancePeriod", [moment(initialValues.performanceDateFrom), moment(initialValues.performanceDateTo)])
        }

        if (initialValues?.tags) {
            form.setFieldValue("tags", initialValues.tags)
        } else {
            form.setFieldValue("tags", [])
        }

        if (initialValues?.dateOfIssue) {
            form.setFieldValue("dateOfIssueForm", moment(initialValues?.dateOfIssue))
        } else {
            form.setFieldValue("dateOfIssueForm", moment())
        }

        if (initialValues?.expectedFullfilmentDate) {
            form.setFieldValue("expectedFullfilmentDateForm", moment(initialValues.expectedFullfilmentDate))
        }
        if (initialValues?.purchaseOrderDocument) {
            setCurrentFile(initialValues?.purchaseOrderDocument)
            form.setFieldValue("purchaseOrderDocument", {
                fileId: initialValues.purchaseOrderDocument.fileId,
                url: initialValues.purchaseOrderDocument.url,
                name: initialValues.purchaseOrderDocument.name,
            })
        }

        if (initialValues?.totalNetPrice && initialValues?.totalGrossPrice) {
            form.setFieldValue("totalTaxPrice", initialValues.totalGrossPrice - initialValues.totalNetPrice)
        }

        if (initialValues?.requestedBy?._id) {
            form.setFieldValue(["requestedBy", "_id"], initialValues.requestedBy._id)
            setLocalRequestedBy(initialValues.requestedBy._id)
            setUserRuleCount(getUserRuleCount(initialValues.requestedBy._id))
        } else {
            form.setFieldValue(["requestedBy", "_id"], loggedInUser?.id)
            setLocalRequestedBy(loggedInUser?.id)
            setUserRuleCount(getUserRuleCount(loggedInUser?.id ?? ""))
        }

        if (initialValues?.approvalProcesses) {
            const nextApproverId = getNextApproverORFirstNotSkippedApprover(initialValues.approvalProcesses)
            form.setFieldValue("nextApprover", nextApproverId)
            setApprovalProcesses(initialValues.approvalProcesses ?? [])
        } else {
            let userRule: UserRule | undefined
            if (localRequestedBy) {
                userRule = employees?.find((employee) => employee.id === localRequestedBy)?.rule?.find((rule) => rule.companyId === company._id)
            } else {
                userRule = loggedInUser?.rule?.find((rule) => rule.companyId === company._id)
            }

            void applyRulesToForm({ rules: userRule })
        }

        if (initialValues?.vendor?._id) {
            form.setFieldValue(["vendor", "_id"], initialValues.vendor._id)
            setLocalVendor(initialValues.vendor._id)
        }

        if (initialValues?.lineItems) {
            const updatedLineItems = initialValues.lineItems.map((item: ILineItem) => ({
                ...item,
                taxRate: { _id: item.taxRate?._id ?? NO_TAX_RATE, taxRate: item.taxRate?.taxRate ?? 0 },
            }))

            form.setFieldValue("lineItems", updatedLineItems)
            onAccountingFieldsRelatedLineItemChange(updatedLineItems)
        }
    }, [initialValues, form, taxValues, localRequestedBy])

    // Safely get proposed approvers with error handling
    const proposedApprovers = useMemo(() => {
        const formData = {
            ...form.getFieldsValue(),
            nextApprover: form.getFieldValue("nextApprover"),
            requestedBy: { _id: requestedBy?._id ?? loggedInUser?.id },
            totalNetPrice: form.getFieldValue("totalNetPrice") ?? 0,
            totalGrossPrice: form.getFieldValue("totalGrossPrice") ?? 0,
            approvalProcesses,
        }

        // Ensure we have valid data before calling getProposedApprovers
        if (!formData || Object.keys(formData).length === 0 || !formData.lineItems) {
            return []
        }

        return getProposedApprovers(formData)
    }, [form, getProposedApprovers, requestedBy, approvalProcesses])

    useEffect(() => {
        form.setFieldValue("taxRate", resolveTax(lineItems))
    }, [form, taxValues, lineItems])

    const applyRulesToForm = ({ rules }: { rules?: AccountingRule }) => {
        const fieldsToValidate: Array<keyof PurchaseOrderFormValues> = []

        if (rules?.costCenter) {
            form.setFieldValue("costCenter", rules?.costCenter)
            fieldsToValidate.push("costCenter")
            updateAllLineItems("costCenter", rules?.costCenter)
        }
        if (rules?.costCenter2) {
            form.setFieldValue("costCenter2", rules?.costCenter2)
            fieldsToValidate.push("costCenter2")
            updateAllLineItems("costCenter2", rules?.costCenter2)
        }
        if (rules?.expenseAccount) {
            form.setFieldValue("expenseAccount", rules?.expenseAccount)
            fieldsToValidate.push("expenseAccount")
            updateAllLineItems("expenseAccount", rules?.expenseAccount)
        }
        if (fieldsToValidate.length > 0) {
            form.validateFields(fieldsToValidate).catch(() => {
                // ignore this will update the UI validation messages
            })
        }

        buildPurchaseOrderApprovalProcess()
    }

    const onVendorChange = async (vendorId: string, updatedVendorRules?: AccountingRule) => {
        // Update local & form state
        setLocalVendor(vendorId)
        form.setFieldValue(["vendor", "_id"], vendorId)

        // Apply rules to form
        const currentVendor = vendorList?.docs?.find((vendor) => vendor.id === vendorId)
        const currentVendorRule = updatedVendorRules ?? currentVendor?.rule
        const currentRequsterId = form.getFieldValue(["requestedBy", "_id"])
        const currentRequster = employees?.find((employee) => employee.id === currentRequsterId)
        const currentRequsterRule = currentRequster?.rule?.find((rule) => rule.companyId === company._id)
        const rulesUpdate = calculateAccountingRules({
            vendorRule: currentVendorRule,
            userRule: currentRequsterRule,
            vendorRulesShouldOverwrite: vendorRulesOverwriteEmployeeRulesEnabled,
        })
        await applyRulesToForm({ rules: rulesUpdate })
    }

    const onRequesterChange = async (requesterId: string) => {
        // Update local & form state
        form.setFieldValue(["requestedBy", "_id"], requesterId)
        const userRulesCount = getUserRuleCount(requesterId)
        setUserRuleCount(userRulesCount)

        // Apply rules to form
        const currentRequster = employees?.find((employee) => employee.id === requesterId)
        const currentRequsterRule = currentRequster?.rule?.find((rule) => rule.companyId === company._id)
        const currentVendorId = form.getFieldValue(["vendor", "_id"])
        const currentVendor = vendorList?.docs?.find((vendor) => vendor.id === currentVendorId)
        const currentVendorRule = currentVendor?.rule
        const rulesUpdate = calculateAccountingRules({
            vendorRule: currentVendorRule,
            userRule: currentRequsterRule,
            vendorRulesShouldOverwrite: vendorRulesOverwriteEmployeeRulesEnabled,
        })
        await applyRulesToForm({ rules: rulesUpdate })
    }

    const onApproverChange = async (approver: string) => {
        await buildPurchaseOrderApprovalProcessWithApprover(approver)
    }

    const handleFileChange = (data: DocumentCoreData) => {
        setCurrentFile(data)
        form.setFieldValue("purchaseOrderDocument", data)
    }

    const handleFileOcrChange = (data: VendorReadFileAiResponse) => {
        setCurrentFileOcr(data)

        const ocrData = data?.ocrData as PurchaseOrderOcrResult
        if (!ocrData) {
            return
        }

        if (ocrData?.items?.length > 0) {
            setLineItems(
                ocrData.items.map((e) => ({
                    name: e.description,
                    materialCode: e.articleNumber,
                    invoiceLinks: [],

                    quantity: e.quantity ?? 0,
                    unitOfMeasure: e.unit,

                    unitPrice: e.unitPrice ?? 0,
                    totalNetPrice: e.totalNetPrice ?? 0,
                    totalTaxPrice: e.totalTaxPrice ?? 0,
                    taxRate: e.vatRate ? ExpenseService.getExpenseTaxRate({ taxRate: e.vatRate }) : { _id: NO_TAX_RATE, taxRate: 0 },
                    totalGrossPrice: e.totalGrossPrice ?? 0,

                    costCenter: form.getFieldValue("costCenter"),
                    costCenter2: form.getFieldValue("costCenter2"),
                    expenseAccount: form.getFieldValue("expenseAccount"),
                    currency: ocrData?.currency,
                })),
            )
        }

        const taxRate = ocrData?.totals?.vatRate ? ExpenseService.getExpenseTaxRate({ taxRate: ocrData?.totals?.vatRate }) : undefined

        const deliveryDate = ocrData?.requestedDeliveryDate ?? ocrData?.orderDate

        if (ocrData?.orderNumber) {
            form.setFieldValue("purchaseOrderNumber", ocrData?.orderNumber)
        }
        if (ocrData?.notes) {
            form.setFieldValue("description", ocrData?.notes)
        }
        if (ocrData?.orderDate) {
            form.setFieldValue("dateOfIssueForm", moment(ocrData?.orderDate))
        }
        if (deliveryDate) {
            form.setFieldValue("expectedFullfilmentDateForm", moment(deliveryDate))
            form.setFieldValue("performancePeriod", [moment(deliveryDate), moment(deliveryDate).endOf("month").endOf("day")])
        }
        if (ocrData?.currency) {
            form.setFieldValue("currency", ocrData?.currency)
        }
        if (ocrData?.totals?.totalNet) {
            form.setFieldValue("totalNetPrice", ocrData?.totals?.totalNet)
        }
        if (taxRate) {
            form.setFieldValue("taxRate", taxRate)
        }
        if (ocrData?.totals?.vatAmount) {
            form.setFieldValue("totalTaxPrice", ocrData?.totals?.vatAmount)
        }
        if (ocrData?.totals?.totalGross) {
            form.setFieldValue("totalGrossPrice", ocrData?.totals?.totalGross)
        }
        if (ocrData?.deliveryAddress) {
            form.setFieldValue("deliveryLocation", {
                addressLine1: ocrData?.deliveryAddress.street,
                zipCode: ocrData?.deliveryAddress.zipCode,
                city: ocrData?.deliveryAddress.city,
                country: ocrData?.deliveryAddress.country,
            })
        }
        if (ocrData?.paymentTerms) {
            form.setFieldValue("paymentTerms", ocrData?.paymentTerms)
        }

        // vendor
        if (data.vendorId) {
            void onVendorChange(data.vendorId)
        }
        if (ocrData?.currency) {
            setCurrency(ocrData?.currency)
        }

        void applyRulesToForm({})
    }

    const handleFileRemove = () => {
        setCurrentFile(undefined)
        form.setFieldValue("purchaseOrderDocument", undefined)
    }

    const handleTotalsChange = (field: string, value: string) => {
        const newValue = parseFloat(value) || 0
        const firstItemTaxRateId = lineItems[0]?.taxRate?._id
        // Only proceed if we have line items and tax rate
        if (!firstItemTaxRateId) {
            return
        }

        const taxRate = taxValues.find((tax) => tax._id === firstItemTaxRateId)
        const taxRateValue = taxRate?.taxRate || 0
        const taxMultiplier = 1 + taxRateValue / 100

        let newNetPrice = 0
        let newTaxPrice = 0
        let newGrossPrice = 0

        // Calculate based on which field was changed
        switch (field) {
            case "totalNetPrice":
                newNetPrice = newValue
                newTaxPrice = newValue * (taxRateValue / 100)
                newGrossPrice = newValue * taxMultiplier
                break
            case "totalTaxPrice":
                newTaxPrice = newValue
                newNetPrice = newValue / (taxRateValue / 100)
                newGrossPrice = newNetPrice + newValue
                break
            case "totalGrossPrice":
                newGrossPrice = newValue
                newNetPrice = newValue / taxMultiplier
                newTaxPrice = newValue - newNetPrice
                break
            default:
                return
        }

        const tolerance = 0.01

        // Update form values only if they're different to avoid infinite loops
        const currentValues = form.getFieldsValue()

        if (Math.abs(currentValues.totalNetPrice - newNetPrice) > tolerance) {
            form.setFieldValue("totalNetPrice", newNetPrice)
        }
        if (Math.abs(currentValues.totalTaxPrice - newTaxPrice) > tolerance) {
            form.setFieldValue("totalTaxPrice", newTaxPrice)
        }
        if (Math.abs(currentValues.totalGrossPrice - newGrossPrice) > tolerance) {
            form.setFieldValue("totalGrossPrice", newGrossPrice)
        }
    }

    const getColumnSpan = () => {
        const hasCostCenters2 = costCenters2.length > 0

        if (hasCostCenters2) {
            return 12
        }

        return 24
    }

    const getUserRuleCount = (requester: string) => {
        const rules = employees?.find((employee) => employee.id === requester)?.rule?.find(({ companyId }) => companyId === company._id) ?? {}
        const numberOfRules = Object.values(rules).filter((rule) => rule !== null).length
        // if rules is not the empty object, it includes the id field and the companyId which are not rules and are therefore not included in the rule count
        return numberOfRules === 0 ? numberOfRules : numberOfRules - 2
    }

    const showEmployeeRuleModal = () => {
        const requester = form.getFieldValue("requestedBy")?._id ?? loggedInUser?.id

        showModal(EmployeeRuleModal, true, {
            isShowing: true,
            employeeIDToUpdate: requester,
            onSubmit: async (rules: Pick<UserRule, "costCenter" | "costCenter2" | "expenseAccount">) => {
                setUserRuleCount(Object.values(rules).filter((rule) => rule !== undefined).length)

                // Apply rules to form
                const currentVendorId = form.getFieldValue(["vendor", "_id"])
                const currentVendor = vendorList?.docs?.find((vendor) => vendor.id === currentVendorId)
                const currentVendorRule = currentVendor?.rule
                const rulesUpdate = calculateAccountingRules({
                    vendorRule: currentVendorRule,
                    userRule: rules,
                    vendorRulesShouldOverwrite: vendorRulesOverwriteEmployeeRulesEnabled,
                })
                await applyRulesToForm({ rules: rulesUpdate })
            },
        })
    }

    const onFieldChange = (field: "costCenter" | "costCenter2" | "expenseAccount", value: string) => {
        updateAllLineItems(field, value)
        if (approvalProcessDependentFields.has(field)) {
            buildPurchaseOrderApprovalProcess()
        }
    }

    const onLineItemChange = (idx: number, formValue: FormValue, field: keyof ILineItem) => {
        updateLineItem(idx, formValue, field)
        switch (field) {
            case "costCenter":
            case "costCenter2":
            case "quantity":
            case "unitPrice":
            case "taxRate":
            case "totalNetPrice":
            case "totalTaxPrice":
            case "totalGrossPrice":
                if (approvalProcessDependentFields.has(field)) {
                    buildPurchaseOrderApprovalProcess()
                }
                break
            default:
                break
        }
    }

    const onRemoveLineItem = (idx: number) => {
        removeLineItem(idx)

        const updatedLineItems = form.getFieldValue("lineItems")
        onAccountingFieldsRelatedLineItemChange(updatedLineItems)
        buildPurchaseOrderApprovalProcess()
    }

    const onAccountingFieldsRelatedLineItemChange = (updatedLineItems: Array<ILineItem>) => {
        if (doAllElementsHaveTheSameFieldValue(updatedLineItems, "costCenter")) {
            form.setFieldValue("costCenter", updatedLineItems[0].costCenter)
        }
        if (doAllElementsHaveTheSameFieldValue(updatedLineItems, "costCenter2")) {
            form.setFieldValue("costCenter2", updatedLineItems[0].costCenter2)
        }
        if (doAllElementsHaveTheSameFieldValue(updatedLineItems, "expenseAccount")) {
            form.setFieldValue("expenseAccount", updatedLineItems[0].expenseAccount)
        }
    }

    const anchorRef = useRef(null)

    const handleClose = () => {
        form.resetFields()
        onCancel()
    }

    const showApprover = isPurchaseOrderApproverVisible(initialValues)

    const isApproverChangeDisabled = !!workflow?._id && !company?.allowWorkflowAdjustments && !AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__ADJUST_WORKFLOW)

    const userLabel = () => (
        <>
            <span className="cursor-pointer" onClick={showEmployeeRuleModal}>
                {`${t("input:request.requested_by")} `}
                <u className="rule-link">{`(${t("label:rules")} ${userRuleCount}/${MINIMUM_EMPLOYEE_RULES + Number(costCenters2.length > 0) + Number(expenseAccounts.length > 0)})`}</u>
            </span>
        </>
    )

    const leftSide = (
        <Form.Item name="purchaseOrderDocument">
            <FileZone onChange={handleFileChange} onOcrChange={handleFileOcrChange} onRemove={handleFileRemove} initialFile={currentFile} />
        </Form.Item>
    )

    const rightSide = (
        <>
            {/* First Row */}
            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Form.Item label={t("purchaseOrders:drawer.poNumber")} name="purchaseOrderNumber" rules={purchaseOrderFormRules.purchaseOrderNumber}>
                        <Input
                            placeholder={t("purchaseOrders:drawer.poNumber")}
                            minLength={PURCHASE_ORDER_NUMBER_MIN_LENGTH}
                            maxLength={PURCHASE_ORDER_NUMBER_MAX_LENGTH}
                            ref={focusCatcher as unknown as LegacyRef<InputRef>}
                        />
                    </Form.Item>
                </Col>

                <Col span={12}>
                    <Form.Item label={t("purchaseOrders:drawer.dateOfIssue")} name="dateOfIssueForm" rules={purchaseOrderFormRules.dateOfIssue}>
                        <DatePicker className="w-full" disabledDate={gobdNonCompliantDates} format={moment.localeData().longDateFormat("L")} allowClear={false} />
                    </Form.Item>
                </Col>
            </Row>

            {/* Second Row - Description */}
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Form.Item name="description" label={t("input:request.description")} key="description" rules={purchaseOrderFormRules.description}>
                        <Input.TextArea
                            data-testid="purchaseOrderDescriptionField"
                            className="leading-normal pt-4"
                            autoSize
                            rows={1}
                            name="description"
                            placeholder={t("placeholder:request.description")}
                            showCount={true}
                            minLength={PURCHASE_ORDER_DESCRIPTION_MIN_LENGTH}
                            maxLength={PURCHASE_ORDER_DESCRIPTION_MAX_LENGTH}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <VendorSelection
                // preserve line breaks
                onVendorChange={onVendorChange}
                selectedVendor={selectedVendor}
                ocrRan={Boolean(currentFileOcr)}
                newVendorSuggestion={mapVendorReadFileAiResponseToVendor(currentFileOcr)}
            />

            {/* Third Row - Card and Service switches inline and tags */}
            <Row align="middle" className="gap-8 mb-8">
                <Row align="middle" className="gap-4">
                    <CreditCardOutlined style={{ color: "#8B8B8B", fontSize: 15, width: 15, height: 15 }} />
                    <span style={{ fontSize: 13, color: "#181C32" }}>{t("purchaseOrders:drawer.createCard")}</span>
                    <Form.Item name="createCard" valuePropName="checked" style={{ marginBottom: 0, marginLeft: "auto" }}>
                        <Switch size="small" disabled={!company.cardsEnabled} />
                    </Form.Item>
                </Row>

                <Row align="middle" className="gap-4">
                    <ServiceIcon />
                    <span style={{ fontSize: 13, color: "#181C32" }}>{t("purchaseOrders:drawer.service")}</span>
                    <Tooltip title={t("purchaseOrders:drawer.letUsKnowIfYourOrderIsForPhysicalGoodsOrAService")}>
                        <InfoCircleOutlined style={{ color: "#8B8B8B", fontSize: 11, width: 11, height: 11, cursor: "help" }} />
                    </Tooltip>
                    <Form.Item name="isService" valuePropName="checked" className="mb-0 ml-auto">
                        <Switch size="small" onChange={(value) => setIsService(value)} />
                    </Form.Item>
                </Row>

                <Row gutter={[16, 16]} className="ml-auto justify-items-end">
                    <Form.Item name="tags" valuePropName="tags" className="mb-0 flex flex-row gap-4" rules={purchaseOrderFormRules.tags}>
                        <PurchaseOrderTags
                            purchaseOrderTags={tags}
                            onTagChange={(id: string, detach: boolean) => {
                                const purchaseOrderTags = detach ? tags.filter((tag: string) => tag !== id) : [...tags, id]
                                form.setFieldValue("tags", purchaseOrderTags)
                            }}
                        />
                    </Form.Item>
                </Row>
            </Row>

            {/* Fourth Row - Performance Date and Period */}
            <Row align="middle" gutter={[16, 16]}>
                <Col span={12}>
                    <Form.Item
                        label={isService ? t("purchaseOrders:drawer.expectedServiceDate") : t("purchaseOrders:drawer.expectedDeliveryDate")}
                        name="expectedFullfilmentDateForm"
                        rules={purchaseOrderFormRules.expectedFullfilmentDate}
                    >
                        <DatePicker format={moment.localeData().longDateFormat("L")} allowClear={true} className="w-full" disabledDate={gobdNonCompliantDates} />
                    </Form.Item>
                </Col>

                <Col span={12}>
                    <Form.Item label={t("purchaseOrders:drawer.performancePeriod")} name="performancePeriod" rules={purchaseOrderFormRules.performancePeriod}>
                        <DatePicker.RangePicker
                            placeholder={[t("purchaseOrders:drawer.startDate"), t("purchaseOrders:drawer.endDate")]}
                            className="w-full"
                            format={moment.localeData().longDateFormat("L")}
                            allowClear={true}
                            disabledDate={gobdNonCompliantDates}
                        />
                    </Form.Item>
                </Col>
            </Row>

            {/* Sixth Row - Total Net, Tax, Gross */}
            <Row align="middle" gutter={[16, 16]}>
                <Col span={4} data-testid="totalNetPrice">
                    <Form.Item name="totalNetPrice" label={t("purchaseOrders:drawer.totalNet")} required>
                        <CurrencyInput
                            currency={currency}
                            value={initialValues?.totalNetPrice ?? 0}
                            onChange={(e) => handleTotalsChange("totalNetPrice", e.toString())}
                            isDisabled={true}
                        />
                    </Form.Item>
                </Col>

                <FormTaxRateSelector
                    data-testid="itemTax"
                    elem={{
                        name: ["taxRate"],
                        label: t("input:request.tax_rate"),
                        key: "taxRate",
                        value: resolveTax(lineItems),
                        isRequired: false,
                        isDisabled: true,
                        isBordered: false,
                        getPopupAnchor: getPopupAnchor(),
                        taxValues,
                    }}
                    span={4}
                />

                <Col span={8}>
                    <Form.Item name="totalTaxPrice" label={t("purchaseOrders:drawer.vat")}>
                        <CurrencyInput
                            currency={currency}
                            value={initialValues?.totalTaxPrice ?? 0}
                            onChange={(e) => handleTotalsChange("totalTaxPrice", e.toString())}
                            isDisabled={true}
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        required={true}
                        name="totalGrossPrice"
                        label={
                            <>
                                {t("input:request.total_gross_amount")}
                                {<HoverableInfo message={t("tooltips:hospitality.total_calculated_amount")} type="info" />}
                            </>
                        }
                        rules={purchaseOrderFormRules.totalGrossPrice}
                        key="totalGrossPrice"
                    >
                        <CurrencyInput
                            currency={currency}
                            value={initialValues?.totalGrossPrice ?? 0}
                            onChange={(e) => handleTotalsChange("totalGrossPrice", e.toString())}
                            isDisabled={false}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <LineItemsList
                lineItems={lineItems}
                expandedItems={expandedItems}
                onAdd={addLineItem}
                onRemove={onRemoveLineItem}
                onToggleExpand={toggleExpand}
                setExpandedItems={setExpandedItems}
                onChange={onLineItemChange}
                type="purchase-order"
                currency={currency}
                form={form}
            />

            <Row gutter={[16, 16]}>
                <Col span={getColumnSpan()}>
                    <Form.Item
                        label={t("input:request.cost_center")}
                        key="costCenter"
                        name="costCenter"
                        rules={[{ required: isCostCenterUniform, message: t("validation:required") }]}
                    >
                        <CostCenterSelect
                            costCenters={costCenters}
                            showCostCentersIds={company.showCostCentersIds}
                            getPopupAnchor={getPopupAnchor}
                            onSelect={(value) => onFieldChange("costCenter", value)}
                            disabled={!isCostCenterUniform}
                            placeholder={!isCostCenterUniform ? t("label:splitted") : t("placeholder:cost_center.select_a_cost_center")}
                            notFoundContent={t("missing:no_cost_center")}
                            data-testid="finway:purchaseOrder:costCenterSelect"
                        />
                    </Form.Item>
                </Col>

                <Col span={costCenters2.length > 0 ? getColumnSpan() : 0}>
                    {costCenters2.length > 0 ? (
                        <Form.Item label={t("purchaseOrders:drawer.costCenter2")} key="costCenter2" name={`costCenter2`} help=" ">
                            <CostCenter2Select
                                costCenters2={costCenters2}
                                getPopupAnchor={getPopupAnchor}
                                onSelect={(value) => onFieldChange("costCenter2", value)}
                                disabled={!isCostCenter2Uniform}
                                placeholder={!isCostCenter2Uniform ? t("label:splitted") : t("placeholder:cost_center_2.select_a_cost_center_2")}
                                notFoundContent={t("missing:no_cost_center_2")}
                            />
                        </Form.Item>
                    ) : null}
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col span={expenseAccounts.length > 0 ? getColumnSpan() : 0}>
                    {expenseAccounts.length > 0 ? (
                        <Form.Item key="expenseAccount" name="expenseAccount" label={t("input:request.expense_account")} help=" ">
                            <ExpenseAccountSelect
                                expenseAccounts={expenseAccounts}
                                onSelect={(value) => onFieldChange("expenseAccount", value)}
                                disabled={!isExpenseAccountUniform}
                                placeholder={!isExpenseAccountUniform ? t("label:splitted") : t("placeholder:expense_account.select_an_expense_account")}
                            />
                        </Form.Item>
                    ) : null}
                </Col>

                <Col span={expenseAccounts.length && costCenters2.length ? 12 : 24}>
                    <CurrencySelect
                        name="currency"
                        initialValue={initialValues?.currency ?? globalCurrency}
                        onCurrencyChange={({ currency: curr }) => {
                            setCurrency(curr)
                            form.setFieldValue("currency", curr)
                        }}
                    />
                </Col>
            </Row>

            <Row>
                <Col span={12}>
                    {/* Delivery location */}
                    <PurchaseOrderAddressAutocomplete
                        label={t("label:delivery_address")}
                        name="deliveryLocation"
                        placeholder={`${t("placeholder:company_settings.address_line")} (${t("placeholder:company_settings.zip")} ${t("placeholder:company_settings.city")} ${t("placeholder:company_settings.country")})`}
                        onSelect={(e) => {
                            form.setFieldValue("deliveryLocation", e)
                        }}
                        initialValue={initialValues?.deliveryLocation ? formatAddress(initialValues?.deliveryLocation) : ""}
                        rules={purchaseOrderFormRules.deliveryLocation}
                    />
                </Col>

                <Col span={12}>
                    <Form.Item label={t("label:payment_terms")} name="paymentTerms" rules={purchaseOrderFormRules.paymentTerms}>
                        <Input minLength={PURCHASE_ORDER_PAYMENT_TERMS_MIN_LENGTH} maxLength={PURCHASE_ORDER_PAYMENT_TERMS_MAX_LENGTH} />
                    </Form.Item>
                </Col>
            </Row>

            {showApprover ? (
                <Row gutter={[16, 16]} className="flex-wrap">
                    {workflow && workflow._id ? (
                        <Col span={12}>
                            <Form.Item label={t("input:request.workflow")} name="workflow" key="workflow">
                                <Select
                                    disabled
                                    placeholder={t("input:request.workflow")}
                                    data-testid="finway:purchaseOrder:workflowSelect"
                                    defaultValue={workflow.name}
                                    key={workflow._id}
                                />
                            </Form.Item>
                        </Col>
                    ) : null}
                    <Col span={workflow ? 12 : 24} className="flex-grow" flex={1} ref={anchorRef}>
                        <Form.Item
                            labelCol={{ className: "h-30" }}
                            label={
                                <div className="flex gap-10 h-30">
                                    <p className="inline-block">{t("input:request.next_approver")}</p>
                                    {approvalProcesses ? (
                                        <ApprovalProcessIndicatorTag
                                            scope={ApprovalScopeEnum.PURCHASE_ORDER}
                                            approvalProcesses={approvalProcesses!}
                                            showLoadingSpinner={isBuildingApprovalProcess}
                                            anchorRef={anchorRef}
                                        />
                                    ) : null}
                                </div>
                            }
                            name="nextApprover"
                            rules={[{ required: true, message: t("validation:required") }]}
                        >
                            <Select
                                showSearch
                                placeholder={t("purchaseOrders:drawer.selectNextApprover")}
                                allowClear
                                filterOption={(input, option) => ((option?.label ?? "") as string).toLowerCase().includes(input.toLowerCase())}
                                onSelect={(v) => onApproverChange(v)}
                                data-testid="finway:purchaseOrder:nextApproverSelect"
                                getPopupContainer={(trigger) => getPopupAnchor()(trigger)}
                                disabled={isApproverChangeDisabled}
                            >
                                {proposedApprovers.length > 0
                                    ? proposedApprovers.map((employee) => {
                                          const substitutionTooltip = getSubstitutionTooltipMessage(employee, loggedInUser?.id ?? "", t)
                                          return (
                                              <Select.Option key={employee.id} value={employee.id} label={`${employee.firstName} ${employee.lastName}`}>
                                                  <div className="flex items-center ml-3" id={`approver-option-${employee.id}`}>
                                                      <UserImage user={employee} showReplacementIcon={!!substitutionTooltip} tooltipTitle={substitutionTooltip} />
                                                      <Tooltip title={substitutionTooltip}>
                                                          <span>{`${employee.firstName} ${employee.lastName}`}</span>
                                                      </Tooltip>
                                                  </div>
                                              </Select.Option>
                                          )
                                      })
                                    : null}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
            ) : null}

            <Row gutter={[16, 16]} className={!isUserAllowedToCreatePOsForOthers ? "hidden" : ""}>
                <Col span={24}>
                    <Form.Item label={userLabel()} name={["requestedBy", "_id"]} rules={[{ required: true, message: t("validation:required") }]}>
                        <Select
                            getPopupContainer={(trigger) => getPopupAnchor()(trigger)}
                            showSearch
                            placeholder={t("purchaseOrders:drawer.selectRequester")}
                            allowClear
                            filterOption={(input, option) => ((option?.label ?? "") as string).toLowerCase().includes(input.toLowerCase())}
                            onSelect={(v) => onRequesterChange(v)}
                        >
                            {eligibleRequesters
                                ?.filter((emp) => !emp.activeCompanyProfile.deleted)
                                .map((employee) => (
                                    <Select.Option key={employee.id} value={employee.id} label={`${employee.firstName} ${employee.lastName}`}>
                                        <div className="flex items-center gap-8">
                                            <UserImage user={employee} />
                                            <span>{`${employee.firstName} ${employee.lastName}`}</span>
                                        </div>
                                    </Select.Option>
                                ))}
                        </Select>
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={[16, 16]} justify="end" className="mt-8 gap-16">
                <Button type="default" onClick={handleClose} style={{ background: "#F3F4F6", borderColor: "#E5E7EB" }}>
                    {t("action:cancel")}
                </Button>
                {!editMode && (
                    <Button
                        type="default"
                        loading={createPurchaseOrderLoading}
                        onClick={onSubmit({ createPurchaseOrderFn: createPO, checkAndExpandErrors, shouldCheckAbsentApprovers: true })}
                        style={{ background: "#F3F4F6", borderColor: "#E5E7EB" }}
                        disabled={disabled}
                    >
                        <SaveAsDraftIcon />
                        {t("purchaseOrders:drawer.saveAsDraft")}
                    </Button>
                )}
                <Button
                    type="primary"
                    loading={createAndSubmitPurchaseOrderLoading || updatePurchaseOrderLoading}
                    onClick={onSubmit({ createPurchaseOrderFn: createAndSubmitPO, checkAndExpandErrors, shouldCheckAbsentApprovers: true })}
                    data-testid="finway:purchaseOrder:submitOrderButton"
                    disabled={disabled}
                >
                    {editMode ? t("purchaseOrders:drawer.updateOrder") : t("purchaseOrders:drawer.submitOrder")}
                </Button>
            </Row>
        </>
    )

    return (
        <>
            <Form layout="vertical" form={form} className="p-12" initialValues={initialValues}>
                <ResponsiveFormLayout leftSide={leftSide} rightSide={rightSide} drawerMode={true} />
            </Form>
            <ReplacementForAbsentApproverModal
                id="replacementApprover"
                availableReplacementsForAbsentApprover={availableReplacementsForAbsentApprover}
                absentApprovers={absentApprovers}
                isVisible={isReplacementForAbsentApproverShowing}
                closeModal={closeReplacementModal}
                onSubmit={
                    pendingCreatePORef.current ? onSubmit({ createPurchaseOrderFn: pendingCreatePORef.current, checkAndExpandErrors, shouldCheckAbsentApprovers: false }) : () => {}
                }
                setCheckIfApproverInAbsence={setCheckIfApproverInAbsence}
                setReplacementForAbsentApprover={setReplacementForAbsentApprover}
                isPurchaseRequest={false}
            />
        </>
    )
}
